import React, { useState, useEffect, useCallback } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTenantContext } from "../../contexts/TenantContext";
import SideNavigation from "./SideNavigation";
import NavigationHeader from "./NavigationHeader";
import Footer from "./Footer";
import OnboardingWizard from "../onboarding/OnboardingWizard";
import WelcomeTour, {
  shouldShowTour,
  markTourComplete,
} from "../onboarding/WelcomeTour";
import HelpPanel from "../help/HelpPanel";
import Icon from "../AppIcon";

const SIDEBAR_COLLAPSED_KEY = "rocket_tronc_sidebar_collapsed";

// Pages that regular employees (User role) can access
const EMPLOYEE_ALLOWED_PATHS = [
  "/my-tronc",
  "/settings/profile",
];

const AppLayout = () => {
  const { isAuthenticated, loading, user, isTenantAdmin } = useAuth();
  const { currentTenant } = useTenantContext();
  const location = useLocation();
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === "true";
  });

  // Onboarding & tour state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Check if user is a regular employee
  const isRegularEmployee = user?.role === "User";

  // Persist sidebar collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Determine if onboarding wizard should show
  useEffect(() => {
    if (
      !loading &&
      isAuthenticated &&
      isTenantAdmin &&
      user?.role !== "GlobalAdmin" &&
      currentTenant &&
      currentTenant.hasCompletedOnboarding === false
    ) {
      setShowOnboarding(true);
    }
  }, [loading, isAuthenticated, isTenantAdmin, user, currentTenant]);

  // Show tour after onboarding completes (if not already seen)
  const handleOnboardingComplete = useCallback(() => {
    setShowOnboarding(false);
    if (shouldShowTour()) {
      setShowTour(true);
    }
  }, []);

  const handleOnboardingSkip = useCallback(() => {
    setShowOnboarding(false);
    if (shouldShowTour()) {
      setShowTour(true);
    }
  }, []);

  const handleTourComplete = useCallback(() => {
    setShowTour(false);
  }, []);

  // Show loading spinner while checking auth
  if (loading) {
    console.log("[AppLayout] Showing loading spinner");
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log("[AppLayout] Not authenticated, redirecting to /login");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // For regular employees, check if they're trying to access allowed pages
  if (isRegularEmployee) {
    const isAllowedPath = EMPLOYEE_ALLOWED_PATHS.some(
      (path) => location.pathname === path || location.pathname.startsWith(path + "/")
    );

    // Also allow root path - will redirect to my-tronc
    if (location.pathname === "/" || location.pathname === "/dashboard-overview") {
      console.log("[AppLayout] Regular employee accessing root, redirecting to /my-tronc");
      return <Navigate to="/my-tronc" replace />;
    }

    if (!isAllowedPath) {
      console.log("[AppLayout] Regular employee trying to access restricted page, redirecting to /my-tronc");
      return <Navigate to="/my-tronc" replace />;
    }
  }

  console.log("[AppLayout] Authenticated, rendering app layout");

  // Main content padding matches the collapsed sidebar width
  // When sidebar is hover-expanded, it overlays the content (fixed positioning)
  const sidebarWidth = sidebarCollapsed ? "lg:pl-16" : "lg:pl-64";

  const handleToggleSidebar = () => {
    setSideNavOpen(!sideNavOpen);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Onboarding Wizard */}
      {showOnboarding && currentTenant && (
        <OnboardingWizard
          tenant={currentTenant}
          user={user}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Welcome Tour */}
      {showTour && <WelcomeTour onComplete={handleTourComplete} />}

      {/* Side Navigation */}
      <SideNavigation
        isOpen={sideNavOpen}
        onClose={() => setSideNavOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarWidth}`}>
        {/* Status Bar at the top */}
        <NavigationHeader
          isCollapsed={sidebarCollapsed}
          onToggleSidebar={handleToggleSidebar}
        />

        {/* Page Content with top padding for fixed header and bottom padding for fixed footer */}
        <main className="min-h-screen pt-12 pb-16">
          <Outlet />
        </main>
      </div>

      {/* Fixed Footer */}
      <Footer />

      {/* Help FAB button */}
      {!showOnboarding && !showTour && (
        <button
          onClick={() => setShowHelp(true)}
          className="fixed bottom-20 right-6 z-[1000] w-12 h-12 rounded-full bg-primary text-white shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center transition-all"
          title="Help & Support"
        >
          <Icon name="HelpCircle" size={22} />
        </button>
      )}

      {/* Help Panel */}
      <HelpPanel isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

export default AppLayout;
